import { styled } from '@mui/material';
import { useId } from 'react';

import type { ResourceColorPalette } from '../../../theme/tokens/resourcesColors';
import { Stack } from '../../atoms/Stack';
import { type ResourcePreviewListProps, useResourcePreviewList } from './useResourcePreviewList';

type Props = ResourcePreviewListProps;

type OwnerState = {
  resourceColor: ResourceColorPalette;
};

const PreviewRoot = styled('section')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  backgroundColor: ownerState.resourceColor.light,
  borderRadius: theme.shape.borderRadius,
  paddingBlock: theme.spacing(1),
  paddingInline: theme.spacing(2),
}));

const PreviewHeading = styled('h3')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.body2Emphasis,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  margin: 0,
  color: ownerState.resourceColor.dark,
}));

const PreviewItems = styled('ul')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  margin: 0,
  marginTop: theme.spacing(0.5),
  paddingLeft: theme.spacing(3),
  '& > li::marker': {
    color: ownerState.resourceColor.main,
  },
}));

const PreviewItem = styled('li')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.primary,
  overflowWrap: 'anywhere',
}));

const ItemMeta = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

const Overflow = styled('p')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...theme.typography.caption,
  margin: 0,
  marginTop: theme.spacing(0.5),
  paddingLeft: theme.spacing(3),
  color: ownerState.resourceColor.dark,
}));

export function ResourcePreviewList(props: Props) {
  const hook = useResourcePreviewList(props);
  const headingId = useId();
  const IconComponent = hook.IconComponent;
  const ownerState = { resourceColor: hook.resourceColor };

  if (props.items.length === 0) {
    return null;
  }

  return (
    <PreviewRoot
      aria-labelledby={headingId}
      ownerState={ownerState}
      data-testid={props['data-testid'] ?? 'resource-preview-list'}
    >
      <PreviewHeading id={headingId} ownerState={ownerState}>
        {IconComponent ? <IconComponent fontSize="small" /> : null}
        {props.title}
      </PreviewHeading>
      <PreviewItems ownerState={ownerState}>
        {hook.visibleItems.map((item) => (
          <PreviewItem key={item.id}>
            <Stack direction="row" spacing={0.5} alignItems="center" wrap="wrap">
              {item.tag}
              <span>{item.label}</span>
              {item.meta ? <ItemMeta>{item.meta}</ItemMeta> : null}
            </Stack>
          </PreviewItem>
        ))}
      </PreviewItems>
      {hook.overflowLabel ? (
        <Overflow ownerState={ownerState}>{hook.overflowLabel}</Overflow>
      ) : null}
    </PreviewRoot>
  );
}

export type { Props as ResourcePreviewListProps };
